/**
 * Mail dashboard translations
 */

export const mail = {
  types: {
    mailbox: 'Mailbox',
    alias: 'Alias',
    list: 'Mailing List',
    shared: 'Shared Mailbox'
  },

  domainSection: {
    provision: {
      title: 'Enable Email',
      description: 'Enable email features for this domain',
      button: 'Enable Email',
      provisioning: 'Enabling...',
      success: 'Email has been enabled',
      error: 'Failed to enable email'
    },
    verify: {
      title: 'Verify your domain',
      description:
        'This domain is not registered with us. Add the _itsh-mail TXT record listed above at your DNS provider so we can see it is yours. Email is set up once it is verified.',
      button: 'Check now',
      verifying: 'Checking...',
      success: 'Domain verified',
      missing:
        'The TXT record cannot be found yet. New DNS records can take a while, and we also check automatically every ten minutes.',
      lookupFailed: 'The DNS lookup failed. Please try again in a moment.',
      error: 'Could not verify the domain'
    },
    dns: {
      autoConfigure: 'Auto-configure',
      success: 'DNS records have been configured',
      error: 'Failed to configure DNS records'
    },
    dkim: {
      copied: 'DKIM key copied'
    },
    catchall: {
      title: 'Catch-All',
      description: 'Forward all emails to non-existent addresses',
      enabled: 'Catch-All is enabled',
      disabled: 'Catch-All is disabled',
      target: 'Target address',
      selectMailbox: 'Select target mailbox',
      noMailboxes: 'This domain has no mailbox yet — create one first.',
      save: 'Save',
      disable: 'Disable',
      enable: 'Enable',
      saving: 'Saving...',
      success: 'Catch-All has been updated',
      error: 'Failed to update Catch-All'
    },
    stats: {
      title: 'Statistics',
      period: {
        day: 'Today',
        week: 'This Week',
        month: 'This Month'
      },
      messagesIn: 'Received',
      messagesOut: 'Sent',
      spamRejected: 'Spam rejected',
      bytesIn: 'Received',
      bytesOut: 'Sent'
    },
    subdomains: {
      title: 'Mail Subdomains',
      subtitle: 'Enable email for subdomains',
      add: 'Add Subdomain',
      empty: 'No mail subdomains configured',
      table: {
        name: 'Name',
        fullName: 'Full Name',
        dnsStatus: 'DNS Status',
        created: 'Created',
        actions: 'Actions'
      },
      dnsStatus: {
        configured: 'Configured',
        missing: 'Missing',
        partial: 'Partial'
      },
      provision: {
        title: 'Add Subdomain',
        nameLabel: 'Subdomain Name',
        namePlaceholder: 'support',
        preview: 'Preview',
        reservedWarning: 'Some names are reserved (www, mail, smtp, imap, pop, ftp, ns, mx, webmail, autodiscover, autoconfig)',
        button: 'Create',
        success: 'Mail subdomain has been created',
        error: 'Failed to create mail subdomain',
        dkimHint: 'This DKIM key must be configured as a DNS record.'
      },
      detail: {
        subtitle: 'Configure mail subdomain',
        dns: {
          title: 'DNS Records',
          success: 'DNS records have been configured',
          error: 'Failed to configure DNS records'
        },
        catchall: {
          title: 'Catch-All',
          selectMailbox: 'Select target mailbox',
          save: 'Save',
          success: 'Catch-All has been updated',
          error: 'Failed to update Catch-All'
        }
      },
      deprovision: {
        title: 'Remove Mail Subdomain',
        confirm: 'Do you really want to disable email for this subdomain?',
        warning: 'Make sure all email accounts on this subdomain have been deleted.',
        button: 'Remove',
        success: 'Mail subdomain has been removed',
        error: 'Failed to remove mail subdomain'
      },
      errors: {
        invalidName: 'Invalid subdomain name. Only lowercase letters, numbers, and hyphens allowed.',
        reservedName: 'This name is reserved and cannot be used.',
        alreadyProvisioned: 'This subdomain is already enabled for email.',
        hasMailAccounts: 'This subdomain still has email accounts. Delete them first.',
        nameTaken:
          'This subdomain is already on record as a domain of its own and cannot also become a mail subdomain. Contact support if you have questions.'
      }
    }
  },

  dnsSetup: {
    progress: '{done} of {total} required records done',
    todo: 'Your domain needs these records so email arrives and does not end up in spam.',
    allDone: 'All set',
    allDoneText: 'Your domain can send and receive email.',
    autoConfigureHint: 'Your domain uses our nameservers. We can add the missing records for you in one click.',
    intro: "Add these records in your domain's DNS settings. You usually find them at the provider you bought the domain from.",
    propagation: 'New records can take up to an hour to show as valid here.',
    recheck: 'Check now',
    required: 'Required records',
    optional: {
      title: 'Optional: setup in mail programs',
      description: 'You do not need these records to send and receive email. They help mail programs find the right settings on their own.',
      count: '{done} of {total} valid'
    },
    state: {
      ok: 'Valid',
      wrong: 'Not right',
      missing: 'Missing',
      unknown: 'Could not check'
    },
    fields: {
      type: 'Type',
      name: 'Name/Host',
      priority: 'Priority',
      weight: 'Weight',
      port: 'Port',
      target: 'Target',
      value: 'Value'
    },
    copy: {
      label: 'Copy {field}',
      title: '{field} copied',
      message: "Now paste it into your DNS provider's form."
    },
    hints: {
      fullName: 'Full name: {host}',
      apex: "{'@'} stands for the domain itself. Some providers want an empty field here instead.",
      trailingDot: 'Some providers need a dot at the end: {example}',
      srvFields: 'Does your provider have separate fields for service and protocol? Then enter {service} and {protocol} separately.',
      unknown: 'We could not check this record just now. Try again later with "Check now".'
    },
    pitfalls: {
      mx: 'Did you use another email provider before? Delete its MX records, or email keeps going there.',
      spf: 'Is there already a record starting with v=spf1? Then add {part} to it exactly as shown, ahead of its all (for example before ~all), instead of creating a second one. With two such records, neither counts.',
      dmarc: 'Is there already a record at _dmarc? Then replace it with this one. With two DMARC records, neither counts.'
    },
    found: 'Found at your provider',
    reasons: {
      duplicate: 'There are several records of this kind here, but there may only be one. Keep only the record with the value above and delete the others.',
      duplicateSuggested: 'These records cancel each other out. Delete all of them and create only this one instead. It combines your records and adds ours. Where their DMARC policies differ, the strictest one applies.',
      extraMx: 'Other mail servers are listed next to ours. Delete them, or email keeps going there.',
      mismatch: 'This record has a different value. Replace it with the value above.',
      mismatchSuggested: 'Replace your record with this one. It keeps your settings and adds ours.',
      spfMismatch: 'Your SPF record does not allow our server yet. Add {part} exactly as shown, ahead of its all (for example before ~all). If it is already after it, it does not count: move it forward. Do not create a second SPF record.',
      spfDuplicate: 'We could not merge these SPF records automatically. Combine them yourself into a single SPF record: keep the mechanisms from all of them (such as include: or ip4:), add {part}, then delete the others. If you keep only one of them, the senders listed in the rest may no longer send for your domain.',
      dkimDuplicate: 'There are several keys under this name. Delete all except the one shown above.',
      cname: 'This name points to another name via CNAME. Remove the CNAME so our record applies.',
      dmarcCname: 'This name points to another name via CNAME, often a service for DMARC reports. Remove the CNAME. Or make sure the record there also contains our report address {address}.'
    },
    errors: {
      partial: 'Some records could not be added. The cards show what went through. Try again in a moment.',
      busy: 'Automatic setup is already running for this domain. Wait a moment, then check again.',
      rateLimited: 'Too many requests in a short time. Wait a minute, then try again.',
      transferPending: "This domain's transfer has not completed yet, so automatic setup is not available. Until then, add the records at your current DNS provider."
    },
    purpose: {
      mx: {
        title: 'Receive email',
        description: 'Tells other servers where to deliver email for your domain.'
      },
      spf: {
        title: 'Allow senders (SPF)',
        description: 'Allows our server to send email for your domain. Without it, your email often lands in spam.'
      },
      dkim: {
        title: 'Sign email (DKIM)',
        description: 'Holds the key recipients use to check that an email really comes from your domain.'
      },
      dmarc: {
        title: 'Protect against forgery (DMARC)',
        description: 'Tells recipients what to do with forged email sent in your name.'
      },
      autoconfig: {
        title: 'Automatic setup (Thunderbird & co.)',
        description: 'Helps Thunderbird and other mail programs find the server settings on their own.'
      },
      autodiscover: {
        title: 'Automatic setup (Outlook)',
        description: 'Helps Outlook find the server settings on its own.'
      },
      srv_autodiscover: {
        title: 'Automatic setup (Outlook)',
        description: 'Another way for Outlook to find the server settings.'
      },
      srv_imaps: {
        title: 'Fetch email (IMAP)',
        description: 'Tells mail programs where to fetch your email.'
      },
      srv_submission: {
        title: 'Send email (SMTP)',
        description: 'Tells mail programs which server to send your email through.'
      },
      verify: {
        title: 'Domain verification',
        description: 'Shows us the domain is yours. Email is set up for it only after that.'
      },
      generic: {
        title: '{type} record',
        description: 'Needed for email on your domain.'
      }
    }
  },

  accounts: {
    title: 'Email Accounts',
    subtitle: 'Manage email accounts and aliases',
    add: 'Create Account',
    search: 'Search...',
    filter: {
      all: 'All',
      mailbox: 'Mailboxes',
      alias: 'Aliases',
      list: 'Lists',
      shared: 'Shared'
    },
    table: {
      email: 'Email',
      type: 'Type',
      usage: 'Storage',
      status: 'Status',
      actions: 'Actions'
    },
    status: {
      active: 'Active',
      inactive: 'Inactive'
    },
    actions: {
      edit: 'Edit',
      viewDetails: 'View Details',
      delete: 'Delete',
      toggleActive: 'Toggle Status'
    },
    empty: {
      title: 'No Email Accounts',
      description: 'Create a new email account to get started.'
    },
    deleteModal: {
      title: 'Delete Account',
      message: 'Are you sure you want to delete {email}? This action cannot be undone.',
      confirmLabel: 'Enter email address',
      confirmPlaceholder: 'Enter email to confirm',
      cancel: 'Cancel',
      delete: 'Delete',
      deleting: 'Deleting...',
      success: 'Account has been deleted',
      error: 'Failed to delete account'
    }
  },

  createModal: {
    title: 'Create Email Account',
    tabs: {
      mailbox: 'Mailbox',
      alias: 'Alias',
      list: 'Mailing List',
      shared: 'Shared'
    },
    fields: {
      name: 'Name',
      namePlaceholder: 'user',
      domain: 'Domain',
      password: 'Password',
      passwordPlaceholder: 'Enter password',
      confirmPassword: 'Confirm Password',
      confirmPasswordPlaceholder: 'Repeat password',
      generatePassword: 'Generate',
      quota: 'Storage',
      quotaPresets: {
        1: '1 GB',
        5: '5 GB',
        10: '10 GB',
        25: '25 GB',
        50: '50 GB',
        100: '100 GB'
      },
      targets: 'Target Addresses',
      targetsPlaceholder: 'Enter email and press Enter',
      targetsHint: 'Addresses this alias forwards to',
      members: 'Members',
      membersPlaceholder: 'Enter email and press Enter',
      membersHint: 'Internal mailboxes that are members of this list',
      externalMembers: 'External Members',
      externalMembersPlaceholder: 'Enter external email address',
      externalMembersHint: 'External email addresses that are also members',
      senderAddresses: 'Sender Addresses',
      senderAddressesPlaceholder: 'Additional sender address',
      senderAddressesHint: 'Additional addresses to send from'
    },
    descriptions: {
      mailbox: 'A full email mailbox with IMAP/SMTP access.',
      alias: 'Forwards emails to one or more target addresses.',
      list: 'Distribution list for group emails to multiple recipients.',
      shared: 'Shared mailbox accessible by multiple users.'
    },
    validation: {
      nameRequired: 'Name is required',
      nameInvalid: 'Only letters, numbers, dots and hyphens allowed',
      domainRequired: 'Domain is required',
      passwordRequired: 'Password is required',
      passwordWeak: 'Password is too weak',
      passwordMismatch: 'Passwords do not match',
      targetsRequired: 'At least one target address is required',
      membersRequired: 'At least one member is required',
      emailInvalid: 'Invalid email address'
    },
    cancel: 'Cancel',
    create: 'Create',
    creating: 'Creating...',
    success: 'Email account has been created',
    error: 'Failed to create email account'
  },

  detail: {
    title: 'Mailbox',
    back: 'Back',
    tabs: {
      overview: 'Overview',
      filters: 'Filters',
      vacation: 'Vacation',
      identities: 'Identities',
      appPasswords: 'App Passwords',
      history: 'Login History'
    },
    overview: {
      title: 'Overview',
      email: 'Email Address',
      type: 'Type',
      status: 'Status',
      created: 'Created',
      lastLogin: 'Last Login',
      never: 'Never',
      quota: {
        title: 'Storage',
        used: 'Used',
        of: 'of',
        free: 'free'
      },
      connection: {
        title: 'Connection Settings',
        imap: 'IMAP',
        smtp: 'SMTP',
        server: 'Server',
        port: 'Port',
        security: 'Security',
        username: 'Username',
        password: 'Password',
        autoconfig: {
          title: 'Automatic Setup',
          intro:
            'Most mail apps detect the server settings automatically once you enter your email address and password.',
          appleButton: 'Download Apple Mail profile',
          appleHint:
            'Open in Safari on iPhone, iPad or Mac. iOS / macOS will show a "Not Signed" warning during install — that is expected and the profile is still safe.',
          thunderbird: 'Thunderbird',
          thunderbirdNote: 'Works automatically — just enter your email address and password.',
          outlook: 'Outlook (Windows / Mac)',
          outlookNote:
            'Works automatically. On first setup Outlook shows a one-time "Allow this website to configure server settings? https://autodiscover.itsh.dev" dialog — click Allow.',
          other: 'Android (K-9, FairEmail, Thunderbird for Android)',
          otherNote: 'Works automatically — just enter your email address and password.'
        }
      },
      password: {
        title: 'Change Password',
        current: 'Current Password',
        new: 'New Password',
        confirm: 'Confirm Password',
        change: 'Change Password',
        changing: 'Changing...',
        success: 'Password has been changed',
        error: 'Failed to change password',
        replaces: 'A new password replaces the current one: mail apps that sign in with the old password need the new one.',
        requested: 'Choose the password for this mailbox here. You set it yourself in the control panel, so it never passes through a connected application.',
        currentRequired: 'Enter your current password.',
        signInAgain: 'Sign in again with your new password.'
      },
      alias: {
        title: 'Aliases',
        empty: 'No aliases configured',
        placeholder: 'Alias name',
        success: 'Alias has been created',
        deleteSuccess: 'Alias has been deleted',
        error: 'Failed to create alias',
        deleteError: 'Failed to delete alias'
      }
    },
    vacation: {
      title: 'Vacation Reply',
      description: 'Configure automatic out-of-office reply',
      enabled: 'Vacation reply enabled',
      disabled: 'Vacation reply disabled',
      subject: 'Subject',
      subjectPlaceholder: 'Away until...',
      message: 'Message',
      messagePlaceholder: 'I am currently out of office...',
      from: 'From',
      to: 'Until',
      save: 'Save',
      saving: 'Saving...',
      success: 'Vacation reply has been saved',
      error: 'Failed to save vacation reply',
      loadError: 'Could not load the vacation reply. Try again before changing it.'
    },
    identities: {
      title: 'Sender Identities',
      description: 'Additional email addresses to send from',
      add: 'Add Identity',
      empty: {
        title: 'No Identities',
        description: 'Add additional sender addresses.'
      },
      table: {
        email: 'Email',
        name: 'Name',
        primary: 'Primary',
        verified: 'Verified',
        actions: 'Actions'
      },
      addModal: {
        title: 'Add Identity',
        email: 'Email Address',
        emailPlaceholder: "alias{'@'}domain.com",
        name: 'Display Name',
        namePlaceholder: 'John Doe',
        cancel: 'Cancel',
        add: 'Add',
        adding: 'Adding...',
        success: 'Identity has been added',
        error: 'Failed to add identity'
      },
      delete: {
        title: 'Remove Identity',
        message: 'Do you really want to remove this identity?',
        cancel: 'Cancel',
        confirm: 'Remove',
        success: 'Identity has been removed',
        error: 'Failed to remove identity'
      }
    },
    appPasswords: {
      title: 'App Passwords',
      description: 'Separate passwords for email apps and external services',
      usage: 'An app password stands in for the mailbox password in a mail program; the user name stays the full email address. It works in mail programs only, not for signing in here. Changing the mailbox password revokes every app password. If one may have leaked, change the mailbox password rather than only deleting it.',
      limitReached: 'A mailbox can have at most five app passwords. Delete one you no longer need.',
      add: 'Create App Password',
      empty: {
        title: 'No App Passwords',
        description: 'Create an app password for email clients.'
      },
      table: {
        name: 'Name',
        created: 'Created',
        lastUsed: 'Last Used',
        actions: 'Actions'
      },
      never: 'Never',
      createModal: {
        title: 'Create App Password',
        name: 'Name',
        namePlaceholder: 'e.g. iPhone Mail',
        nameHint: 'A name to identify this password',
        cancel: 'Cancel',
        create: 'Create',
        creating: 'Creating...',
        success: 'App password has been created',
        error: 'Failed to create app password',
        result: {
          title: 'App Password Created',
          message: 'Copy this password now. It will not be shown again.',
          password: 'Password',
          copy: 'Copy',
          copied: 'Copied',
          done: 'Done'
        }
      },
      delete: {
        title: 'Delete App Password',
        message: 'Do you really want to delete this app password? Apps using it will stop working.',
        cancel: 'Cancel',
        confirm: 'Delete',
        success: 'App password has been deleted',
        error: 'Failed to delete app password'
      }
    },
    history: {
      title: 'Login History',
      description: 'Recent logins to this mailbox',
      empty: {
        title: 'No Logins',
        description: 'No logins have been recorded yet.'
      },
      table: {
        time: 'Time',
        ip: 'IP Address',
        protocol: 'Protocol',
        status: 'Status',
        country: 'Country'
      },
      protocols: {
        imap: 'IMAP',
        smtp: 'SMTP',
        pop3: 'POP3',
        jmap: 'JMAP',
        http: 'Webmail',
        webmail: 'Webmail'
      },
      status: {
        success: 'Success',
        failed: 'Failed'
      }
    }
  },

  quota: {
    unlimited: 'Unlimited',
    used: 'Used',
    free: 'Free'
  },

  webmail: {
    login: {
      title: 'Webmail Login',
      subtitle: 'Sign in with your email account',
      email: 'Email Address',
      emailPlaceholder: "user{'@'}domain.com",
      password: 'Password',
      passwordPlaceholder: 'Enter password',
      submit: 'Sign In',
      loggingIn: 'Signing in...',
      error: 'Login failed',
      invalidCredentials: 'Invalid email or password'
    },
    dashboard: {
      title: 'Mailbox',
      logout: 'Sign Out',
      tabs: {
        overview: 'Overview',
        vacation: 'Vacation',
        identities: 'Identities',
        appPasswords: 'App Passwords',
        history: 'History',
        settings: 'Settings'
      },
      settings: {
        title: 'Connection Settings',
        description: 'IMAP and SMTP server information for email clients'
      }
    }
  },

  admin: {
    title: 'Email Management',
    nav: {
      overview: 'Overview',
      accounts: 'Accounts',
      server: 'Server',
      messages: 'Messages',
      sendLimits: 'Sending limits'
    },
    sendLimits: {
      title: 'Sending limits',
      subtitle: 'Review early-lift requests and throttle accounts',
      empty: 'Nothing matches this filter.',
      loadError: 'Could not load the list',
      actionError: 'Action failed',
      throttled: 'Throttled',
      days: '{n} days',
      affected: '{count} mailboxes affected',
      syncFailed: 'Mail server not updated',
      syncFailedHint:
        'The change is saved but could not be pushed to the mail server. Please run the mail-guard-sync task.',
      noSendersAffected: 'The throttle reached no mailbox',
      noSendersAffectedHint:
        'No mailbox was found for this account that the throttle covers. For active abuse, deactivate the account instead.',
      approveBlocked: 'Cannot approve while a throttle is in force. Clear the throttle first.',
      forcedReason: 'Throttle reason: {reason}',
      confirmFor: 'Account: {email}',
      noteLabel: 'Reason',
      notePlaceholder: 'At least 10 characters',
      filter: {
        label: 'Filter',
        pending: 'Open',
        throttled: 'Throttled',
        approved: 'Approved',
        denied: 'Denied',
        all: 'All'
      },
      signals: {
        age: 'Account age',
        mailboxes: 'Mailboxes',
        outbound: 'Sent, 7 days',
        verified: 'Email verified',
        payment: 'Payment method',
        requested: 'Requested'
      },
      actions: {
        approve: 'Approve',
        approveTitle: 'Approve the lift',
        approveSuccess: 'Sending limit lifted',
        deny: 'Deny',
        denyTitle: 'Deny the request',
        denySuccess: 'Request denied',
        restrict: 'Throttle',
        restrictTitle: 'Throttle the account',
        restrictSuccess: 'Account throttled',
        unrestrict: 'Clear throttle',
        unrestrictTitle: 'Clear the throttle',
        unrestrictSuccess: 'Throttle cleared',
        withdraw: 'Withdraw lift',
        withdrawTitle: 'Withdraw the lift',
        withdrawSuccess: 'Lift withdrawn'
      }
    },
    overview: {
      title: 'Email Overview',
      health: {
        title: 'Server Status',
        healthy: 'Healthy',
        degraded: 'Degraded',
        unhealthy: 'Unhealthy',
        unknown: 'Unknown'
      },
      stats: {
        messagesIn: 'Messages Received',
        messagesOut: 'Messages Sent',
        spamRejected: 'Spam Rejected',
        authFailures: 'Failed Logins'
      },
      queue: {
        title: 'Queue',
        pending: 'Pending Messages',
        empty: 'Queue is empty'
      }
    },
    accounts: {
      title: 'All Email Accounts',
      subtitle: 'Manage accounts across all domains',
      table: {
        email: 'Email',
        domain: 'Domain',
        type: 'Type',
        usage: 'Storage',
        status: 'Status',
        lastLogin: 'Last Login',
        owner: 'Owner',
        actions: 'Actions'
      }
    },
    server: {
      title: 'Server Status',
      health: {
        title: 'Components',
        stalwart: 'Mail Server',
        queue: 'Queue',
        reachable: 'Reachable',
        notReachable: 'Not Reachable',
        healthy: 'Healthy',
        unhealthy: 'Problem'
      },
      queue: {
        title: 'Queue',
        table: {
          from: 'From',
          to: 'To',
          subject: 'Subject',
          retries: 'Retries',
          nextRetry: 'Next Retry',
          queued: 'Queued'
        }
      },
      stats: {
        title: 'Statistics',
        period: 'Period',
        total: 'Total',
        perDomain: 'Per Domain'
      }
    },
    messages: {
      title: 'Message Log',
      inbound: 'Inbound',
      outbound: 'Outbound',
      searchPlaceholder: 'Sender, recipient or subject...',
      time: 'Time',
      sender: 'Sender',
      recipient: 'Recipient',
      subject: 'Subject',
      size: 'Size',
      empty: 'No messages recorded.'
    },
    dashboard: {
      totalAccounts: 'Total Accounts',
      activeAccounts: 'Active',
      storage: 'Storage',
      messageFlow: 'Message Flow',
      spamRate: 'Spam Rate',
      period: {
        day: 'Day',
        week: 'Week',
        month: 'Month'
      }
    }
  },

  mailboxDelegation: {
    title: 'Mailbox Delegates',
    subtitle: 'Grant mailbox users management access via the webmail portal',
    addDelegate: 'Add Delegate',
    empty: 'No mailbox delegates configured',
    emptyDescription: 'Add a delegate to grant a mailbox user management access to this domain via the webmail portal.',
    never: 'No expiry',
    permissionGroups: {
      mailbox: 'Mailboxes',
      mailinglist: 'Mailing Lists'
    },
    permissionLabels: {
      'mailbox:read': 'View',
      'mailbox:write': 'Create & Edit',
      'mailbox:delete': 'Delete',
      'mailinglist:read': 'View',
      'mailinglist:write': 'Create & Edit'
    },
    add: {
      title: 'Add Delegate',
      subtitle: 'Grant a mailbox user management permissions',
      email: 'Email',
      emailPlaceholder: "user{'@'}domain.com",
      emailHint: 'The mailbox email address of the user to delegate to',
      permissions: 'Permissions',
      permissionsHint: 'Select which permissions the delegate should have',
      expiry: 'Expiry',
      noExpiry: 'No expiry',
      expiryHint: 'Optionally set an expiry date for this delegation'
    },
    edit: {
      title: 'Edit Delegate',
      subtitle: 'Update delegate permissions and expiry'
    },
    delete: {
      title: 'Remove Delegate',
      confirmText: 'Remove',
      warningText: 'This delegate will lose all management access to this domain via the webmail portal.'
    },
    notifications: {
      added: 'Delegate added',
      addedMessage: '{email} has been granted delegate access',
      accountNotFound: 'No mailbox account found with this email address',
      alreadyDelegate: 'This mailbox user is already a delegate',
      updated: 'Delegate updated',
      updatedMessage: 'Delegate permissions have been updated',
      deleted: 'Delegate removed',
      deletedMessage: '{email} has been removed as delegate'
    }
  },
  billing: {
    activateTitle: 'Activate Email Hosting',
    activateDescription: 'Up to 1,000 mailboxes on this domain. DKIM, SPF and DMARC configured automatically.',
    activate: 'Activate',
    activated: 'Email activated',
    deactivated: 'Email hosting canceled',
    perMonth: '€/month',
    usageTitle: 'Email Usage',
    storage: 'Storage',
    outbound: 'Outbound Messages (Month)',
    projectedOverage: 'Projected overage',
    net: 'net',
    month: 'month',
    includedInHosting: 'Included in hosting',
    grandfathered: 'Legacy plan',
    deactivate: 'Cancel email hosting immediately',
    deactivateWarning: 'Mail delivery for this domain stops immediately and the current billing period is not refunded. Your mailboxes and emails are retained. To cancel at the end of the term, use Packages → Cancel.',
    confirmDeactivate: 'Cancel immediately',
    deactivateGrandfathered: 'End email hosting',
    deactivateGrandfatheredWarning: 'Mail delivery for this domain stops immediately. Your mailboxes and emails are retained. Your free legacy rate ends permanently: reactivating later books paid email hosting at the price current at that time.',
    confirmDeactivateGrandfathered: 'End email',
    includedInHostingHint: 'Email is included in your hosting package and has no contract of its own. As long as the package runs and the domain stays linked to it, email stays active for this domain. You cancel the package itself under Packages.',
    endsOn: 'Canceled as of {date}. Until then email runs as usual, after that it is switched off for this domain. Your mailboxes and emails are retained.',
    deactivateNoticeWarning: 'Your email hosting is already canceled as of {date}. If you end it now, delivery stops immediately instead of on {date}, and the remaining time is not refunded. Your mailboxes and emails are retained.',
    takeoverNote: 'This domain is now linked to a hosting package that includes email. Your paid email hosting will therefore end automatically shortly, and the unused days are refunded pro rata.',
    reactivate: 'Re-enable email',
    customerHoldTitle: 'Email is switched off',
    customerHoldDescription: 'You switched off email for this domain. Your mailboxes and emails are still stored and become active again when you re-enable it. This costs nothing extra.',
    pendingTitle: 'Email is still switched off',
    pendingDescription: 'The domain is entitled to email again. Your mailboxes are switched back on automatically within the hour, or you can switch them on now.',
    switchOnNow: 'Switch email on now',
    paidHoldNote: 'Email for this domain is switched off, but your email hosting is still billed. Switch email back on, or end the email hosting below.',
    customerHoldNote: 'You switched off email for this domain. Your mailboxes and emails are still stored.',
    noEntitlementNote: 'Email for this domain was switched off because it no longer has email hosting. Your mailboxes and emails are still stored and are switched on again as soon as the domain has email hosting.',
    domainDeletedTitle: 'Domain deleted',
    domainDeletedDescription: 'Email is switched off because the domain was deleted. Restore the domain to use email again. Your mailboxes and emails are still stored.',
    goToDomain: 'Go to domain',
    domainSuspendedTitle: 'Domain suspended',
    domainSuspendedDescription: 'The domain is suspended, so email is switched off for now. Once the suspension is lifted and the domain has email hosting, it is switched on again. Your mailboxes and emails are retained.',
    adminHoldTitle: 'Email blocked',
    adminHoldDescription: 'We have blocked email for this domain. Please contact support.',
    errors: {
      takeoverPending: 'Your email hosting is being taken over by the hosting package and ends automatically with a pro-rata refund. There is nothing to cancel.',
      adminHold: 'We have blocked email for this domain. Please contact support.',
      ownerOnly: 'Only the owner of the domain can activate or end email for it.',
      domainSuspended: 'The domain is suspended. Email can only be activated again once the suspension is lifted.',
      domainDeleted: 'The domain was deleted. Restore it first, then you can activate email.',
      reenableFailed: 'Email could not be fully switched on. Please try again in a few minutes.',
      stripeUnavailable: 'Payment processing could not be reached. Please try again in a few minutes.'
    }
  },

  errorCodes: {
    protectedAccount: 'This mailbox is managed by us and cannot be changed here. Contact support if you need a change.',
    ownerMismatch: 'The address and the mailbox or list it belongs to belong to different customers, so they cannot be linked here.',
    aliasTargetFixed: 'This address belongs to its mailbox or list for good. Its target cannot be changed.',
    externalMemberLimit: 'A list or alias can deliver to at most 100 external addresses.',
    invalidSharedMember: 'Members of a shared mailbox must be mailboxes on the same domain.',
    mailServerFailed: 'The mail server did not confirm the change. Try again in a few minutes.',
    filterBusy: "Another change to this mailbox's filters is in progress. Try again in a moment.",
    addressExists: 'This address already exists, possibly switched off.',
    identityOff: 'This address is already an identity of this mailbox but switched off. It comes back on as soon as mail is switched on for its domain.',
    rateLimited: 'Too many changes in a short time. Wait a minute and try again.',
    currentPasswordWrong: 'The current password is not correct.',
    credentialRateLimited: 'Too many attempts. Try again in 15 minutes.'
  },

  entitlement: {
    goToActivation: 'Go to email activation',
    owner: {
      notEntitled: 'This domain has no active email hosting. Activate email for the domain, then you can create and enable addresses.',
      disabled: 'Email is switched off for this domain. Switch email for the domain back on, then you can create and enable addresses.',
      adminHold: 'We have blocked email for this domain. Please contact support.',
      unverified: 'Verify the domain first: add the _itsh-mail TXT record shown in the DNS setup.'
    },
    delegate: {
      notEntitled: 'This domain has no active email hosting. The owner of the domain has to activate email first.',
      disabled: 'Email is switched off for this domain. The owner of the domain has to switch email back on first.',
      adminHold: 'Email is blocked for this domain. Please contact the owner of the domain.',
      unverified: 'The domain is not verified yet. The owner of the domain has to add its _itsh-mail TXT record first.'
    }
  },

  addressTaken: {
    mailbox: 'This address already belongs to a mailbox.',
    shared: 'This address already belongs to a shared mailbox.',
    alias: 'This address already belongs to an alias.',
    list: 'This address already belongs to a mailing list.',
    switchedOff: 'Even switched off, it keeps the address. Delete it to reuse the address.',
    generic: 'This address is already in use.'
  },

  sendLimit: {
    title: 'Sending limit for new accounts',
    description:
      'For the first {days} days after your customer account is registered, {hourly} messages per hour and {daily} per day apply per mailbox.',
    reassurance:
      'Messages beyond that are not lost. They are temporarily rejected and stay in your email client. Depending on the client, delivery is retried automatically, otherwise you send it again.',
    autoLift: 'On {date} the reduced limits end automatically. There is nothing for you to do.',
    daysLeft: '{n} day left | {n} days left',
    statusLabel: {
      none: 'No request',
      pending: 'Open',
      approved: 'Approved',
      denied: 'Denied'
    },
    request: {
      button: 'Request an early lift',
      dialogTitle: 'Request an early lift of the sending limit',
      dialogDescription:
        'We lift the restriction early provided there are no indications of abuse. A member of staff reviews your request.',
      reasonLabel: 'Reason',
      reasonPlaceholder: 'e.g. migrating existing mailboxes from another provider',
      submit: 'Send request',
      success: 'Request sent',
      error: 'Could not send the request'
    },
    status: {
      pending: 'Your request from {date} is being reviewed.',
      pendingShort: 'We will get back to you once the review is complete.',
      approved: 'The restriction has been lifted. The regular sending limits apply.',
      denied: 'Your request was declined.',
      deniedReason: 'Reason: {reason}',
      throttled: 'A reduced sending limit set by us currently applies to your account.',
      throttledHint: 'Please contact support if you have questions about this.'
    }
  },

  filters: {
    title: 'Sieve filters',
    description: 'Automatically sort, forward, or flag incoming messages.',
    create: 'New filter',
    createTitle: 'Create new filter',
    editTitle: 'Edit filter',
    enabled: 'Enabled',
    disabled: 'Disabled',
    deleted: 'Filter deleted',
    savedCreate: 'Filter created',
    savedEdit: 'Filter saved',
    deleteTitle: 'Delete filter?',
    deleteConfirm: 'Filter "{name}" will be permanently removed. Existing messages are not affected.',
    moveUp: 'Move up',
    moveDown: 'Move down',
    fieldNameLabel: 'Filter name',
    conditions: 'Conditions',
    actions: 'Actions',
    addCondition: 'Add condition',
    addAction: 'Add action',
    noConditions: 'No conditions — actions run for every message.',
    stopOnMatch: 'Stop processing further filters when this one matches',
    valuePlaceholder: 'Value',
    sizePlaceholder: 'e.g. 5M or 1024',
    headerNamePlaceholder: 'Header name (e.g. List-Id)',
    folderPlaceholder: 'Folder name (e.g. GitHub)',
    forwardPlaceholder: "forward{'@'}example.com",
    rejectReasonPlaceholder: 'Rejection reason',
    createFolder: 'Create folder if missing',
    negate: 'Negate condition',
    rawTitle: 'Raw Sieve script',
    rawHint: 'Write Sieve directly (RFC 5228). Validated server-side on save.',
    preview: 'Generated Sieve script',
    previewPlaceholder: 'Fill in a condition or action to see the preview.',
    validating: 'validating …',
    validOk: 'Filter is valid',
    previewUnavailable: 'The check cannot run right now. The filter is still checked when you save.',
    format: 'Format',
    formatTitle: 'Format script (Shift+Alt+F)',
    wasmLoading: 'Loading local Sieve validator …',
    wasmReady: 'Local syntax checking active',
    wasmUnavailable: 'Local syntax checking unavailable — server validates on save.',
    mode: {
      builder: 'Builder',
      raw: 'Raw'
    },
    match: {
      all: 'All',
      any: 'Any'
    },
    fields: {
      from: 'Sender (From)',
      to: 'Recipient (To)',
      cc: 'CC',
      subject: 'Subject',
      header: 'Any header',
      body: 'Message body',
      size: 'Size',
      'envelope-from': 'Envelope sender',
      'envelope-to': 'Envelope recipient'
    },
    ops: {
      contains: 'contains',
      is: 'is exactly',
      matches: 'matches (glob)',
      regex: 'regex',
      over: 'larger than',
      under: 'smaller than'
    },
    actionTypes: {
      fileinto: 'Move to folder',
      'copy-fileinto': 'Copy into folder',
      'mark-read': 'Mark as read',
      flag: 'Set flag',
      forward: 'Forward to',
      discard: 'Discard',
      reject: 'Reject with reason',
      stop: 'Stop processing'
    },
    summary: {
      raw: 'Custom Sieve script',
      builder: '{conds} condition(s) ({match}), {acts} action(s)'
    },
    empty: {
      title: 'No filters yet',
      hint: 'Create your first filter to start sorting messages automatically.'
    }
  }
} as const
